import { Control, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import SpanInput from '@/components/ui/custom/dataInput/SpanInput';
import { ResourceFields } from '@/data/services/_deprecated/resourceServices';
import Text from '@/components/ui/custom/dataDisplay/Text';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import { ArrowDownOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import { KeyboardKeys } from '@/utils/Keyboard';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';

type ResourceTitleFormProps = {
  defaultValue?: string;
  control: Control<ResourceFields>;
  editing?: boolean;
  onSubmit?(body: Partial<ResourceFields>): Promise<void>;
  loading?: boolean;
};

export default function ResourceTitleForm({
  defaultValue,
  control,
  editing = false,
  onSubmit,
  loading,
}: ResourceTitleFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const {
    field: title,
    fieldState: { error },
  } = useController({
    control,
    name: 'title',
    defaultValue,
    rules: {
      required: t('form.required', {
        field: t('form.title.title').toLowerCase(),
      }),
      validate: (value?: string | null) => {
        if (!value?.trim())
          return t('form.empty', {
            field: t('form.title.title').toLowerCase(),
          });

        return true;
      },
    },
  });

  const onSave = async () => {
    await onSubmit?.({ title: title.value });
  };

  const onCancel = () => {
    control._reset({ title: defaultValue });
  };

  const hasChanges = defaultValue !== title.value;

  return (
    <section className="flex items-center gap-1 w-full">
      <SpanInput
        {...title}
        className={{
          base: 'flex w-full',
          input: 'flex h-8 border-primary',
        }}
        isEditing={editing}
        errorLabelText={error?.message}
        onKeyDown={event => {
          if (error?.message) return;
          if (event.key === KeyboardKeys.Enter) {
            onSave();
          }
          event.stopPropagation();
        }}
      >
        {value => (
          <Tooltip
            text={value}
            classNameContainer="overflow-x-hidden max-w-96"
            color="lightPurple"
          >
            <Text
              text={value}
              className="font-500 text-16 text-primary truncate"
            />
          </Tooltip>
        )}
      </SpanInput>
      <ConditionalRenderer condition={editing}>
        <Tooltip text={t('save')}>
          <MainButton
            text={t('save')}
            icon={<ArrowDownOnSquareIcon />}
            onClick={onSave}
            disabled={!hasChanges || loading}
            loading={loading}
          />
        </Tooltip>
        <Tooltip text={t('cancel')}>
          <IconButton
            icon={
              <XMarkIcon className="h-5 w-5 stroke-[2.5] text-base-content" />
            }
            onClick={onCancel}
            disabled={!hasChanges || loading}
          />
        </Tooltip>
      </ConditionalRenderer>
    </section>
  );
}
